import { Dropdown } from '@/components/dropdown'
import { FormControl } from '@/components/form-control'
import { Controller, Path, PathValue, useFormContext } from 'react-hook-form'
import {
  SpecialtyOption,
  useSpecialtySearch,
} from '@/modules/salary/filters/use-specialty-search'
import { getFilterDropdownStyles } from '@/modules/salary/filters/desktop/utils'
import { cn } from '@/lib/css-utils'
import { useIsMobile } from '@/utils/screen'
import { forwardRef, type JSX, type Ref, useRef } from 'react'

interface SpecialtyFilterSelectorProps<T extends Record<string, unknown>> {
  name: Path<T>
  onSelectSpecialty?: (specialty: string | null) => void
  className?: string
  label?: string
  professionSlug?: string | null
  variant?: 'filter' | 'select'
  isAuthenticated: boolean
  isLoading?: boolean
}

function SpecialtySelectComponent<T extends Record<string, unknown>>(
  {
    name,
    className,
    label,
    professionSlug,
    onSelectSpecialty,
    variant = 'filter',
    isAuthenticated,
    isLoading,
  }: SpecialtyFilterSelectorProps<T>,
  ref: Ref<HTMLDivElement>, // Adjust based on the actual type of the Dropdown ref
) {
  const isMobile = useIsMobile()
  const hasInitRef = useRef(false)
  const { control, setValue, getValues } = useFormContext<T>()
  const { options, searchSpecialty } = useSpecialtySearch({
    overrideProfessionSlug: professionSlug,
    onSearchSpecialty: (specialties) => {
      if (specialties.length === 1) {
        setValue(name, specialties[0].slug as PathValue<T, Path<T>>)
      }
      const specialtyFieldValue = getValues()[name]
      const foundSpecialty = specialties.find(
        (specialty) => specialty.slug === specialtyFieldValue,
      )
      if (specialtyFieldValue && !foundSpecialty && specialties.length) {
        setValue(name, null as PathValue<T, Path<T>>)
      }
      hasInitRef.current = true
    },
    isAuthenticated,
    isLoading,
  })

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState }) => {
        return (
          <FormControl
            errorText={fieldState.error?.message}
            className={cn('flex-4 min-w-0 h-full', className)}
            label={label}
          >
            <Dropdown
              isInteractive={options?.length > 0}
              ref={ref}
              cacheOptions={false}
              onChange={(dropdownOption) => {
                const firstOption = dropdownOption?.[0] as SpecialtyOption
                onChange(firstOption?.slug ?? null)
                onSelectSpecialty?.(firstOption?.slug ?? null)
              }}
              loadOptions={async (input: string) => {
                return searchSpecialty(input)
              }}
              value={options?.find((op) => op.slug === value)}
              placeholder="Search specialties"
              menuPortalTarget={null}
              isClearable
              id="specialty-filter"
              styles={
                variant === 'filter'
                  ? getFilterDropdownStyles({ isMobile })
                  : {}
              }
            />
          </FormControl>
        )
      }}
    />
  )
}

export const SpecialtySelect = forwardRef(SpecialtySelectComponent) as <
  T extends Record<string, unknown>,
>(
  props: SpecialtyFilterSelectorProps<T> & { ref?: Ref<HTMLDivElement> },
) => JSX.Element
